import { THeaderType } from 'components/Header/Header'
import LinkWithLocale from 'components/LinkWithLocale'
import React, { FC } from 'react'
import arrowRightDropdownItem from 'assets/svg/arrowRightDropdownItem.svg'
import styled from 'styled-components'
import { localesWithoutKorean } from 'constants/locales'
import useTranslation from 'hooks/useTranslation'
import { TFunction } from 'src/providers/TranslateProvider/TranslateProvider'
import { BadgeType } from 'src/types'
import BadgeDropdown from '../BadgeDropdown'
import PreviewBlock from '../DropdownApp/PreviewBlock'
import HatImage from './images/hat.png'
import useLocale from 'hooks/useLocale'
import { DEFAULT_LOCALE, GATSBY_MAIN_URL } from '@constants'

const getOptions = (t: TFunction) => {
  return [
    {
      name: t('COIN-M'),
      text: t('Perpetual futures settled in cryptocurrency as margin (BTC,ETH, LTC & more)'),
      url: `${GATSBY_MAIN_URL}/app/trade`,
      badgeType: BadgeType.Popular,
    },
    {
      name: t('USDT-M'),
      text: t('Perpetual futures settled in stable coins (USDT/USDC/DAI etc)'),
      url: `${GATSBY_MAIN_URL}/app/trade`,
      badgeType: BadgeType.Popular,
    },
    {
      name: t('Simulated'),
      text: t('Risk-free trading with demo funds (No deposit required)'),
      url: 'https://margex.com/blog/risk-free-margin-trading-with-up-to-100x-leverage-on-margex',
      badgeType: BadgeType.New,
    },
    {
      name: t('Demo live'),
      text: t("Explore Margex's award winning user interface without the need to register"),
      url: `${GATSBY_MAIN_URL}/trade/btcusd`,
      badgeType: null,
    },
  ]
}

interface DropdownFuturesProps extends IOwnProps {
  dropdownType:
    | 'app'
    | 'buy crypto'
    | 'futures'
    | 'spot'
    | 'explore trading'
    | 'price alerts'
    | 'staking'
    | 'markets'
    | 'refferal'
}

const DropdownFutures: FC<DropdownFuturesProps> = () => {
  const { t } = useTranslation()
  const { locale } = useLocale()

  return (
    <>
      <PreviewBlock
        title={t('How to start?')}
        preview={HatImage}
        text={t('How to start trading futures on Margex in less than a minute?')}
        isButton
        buttonLabel={t('Learn more')}
        href={
          locale === DEFAULT_LOCALE
            ? `${GATSBY_MAIN_URL}/quick-start-guide/`
            : `${GATSBY_MAIN_URL}/${locale}/quick-start-guide/`
        }
        includeLangsArr={localesWithoutKorean}
      />
      <DropdownItemGridContainer>
        <ItemWrapper>
          {getOptions(t).map((item, index) => (
            <DropdownItem key={index} to={item.url} withLocale={false} replace>
              <NameWrapper>
                <DropdownItemName>{item.name}</DropdownItemName>
                <BadgeDropdown type={item.badgeType} />
              </NameWrapper>
              <DropdownItemText>{item.text}</DropdownItemText>
            </DropdownItem>
          ))}
        </ItemWrapper>
      </DropdownItemGridContainer>
    </>
  )
}

export default DropdownFutures

const DropdownItemGridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 0.875rem;
  column-gap: 2.75rem;
  background: linear-gradient(0deg, #f3f7fd, #f3f7fd), #123482;
  border-radius: 0 0.5rem 0.5rem 0;
`

const ItemWrapper = styled.div`
  display: grid;
  grid-template-rows: max-content;
  grid-auto-rows: max-content;
  grid-column-gap: 3rem;
  padding: 2rem;
  padding-bottom: 0;
`

const DropdownItem = styled(LinkWithLocale)`
  display: block;
  position: relative;
  width: 330px;
  padding: 0.625rem 0.75rem;
  text-align: start;
  clear: both;
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.normal};
  color: ${({ theme: { colors } }) => colors.dropdownItem};
  white-space: nowrap;
  background-color: transparent;
  border: 0;
  line-height: 1.375;
  transition: color 0.2s, background-color 0.2s;

  &:hover {
    > h6 {
      color: ${({ theme: { colors } }) => colors.link};
    }

    text-decoration: none;
    background-color: ${({ theme: { colors } }) => colors.white};

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 2rem;
      border-radius: 0.25rem;
      background-image: url(${arrowRightDropdownItem});
      background-repeat: no-repeat;
      background-position: center;
      background-color: ${({ theme: { colors } }) => colors.newLightColor.vividBlue};

      [dir='rtl'] & {
        left: 0;
        right: unset;
        transform: rotate(180deg);
      }
    }
  }
`
const DropdownItemName = styled.span`
  color: ${({ theme: { colors } }) => colors.newLightColor.softNavy};
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.semibold};
  font-size: 0.875rem;
  margin-inline-end: 0.5rem;
`
const DropdownItemText = styled.p`
  width: 270px;
  margin: 0;
  margin-top: 2px;
  margin-inline-end: auto;
  white-space: pre-wrap;
  color: ${({ theme: { colors } }) => colors.newLightColor.softNavy};
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.normal};
  font-size: 0.875rem;
  opacity: 0.8;
`
const NameWrapper = styled.div`
  align-items: center;
  text-align: start;
`

interface IOwnProps {
  name?: string
  options: Array<{ name: string; url: string }>
  warning?: string | null
  type: THeaderType
}
